import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { PopoverCellEditor } from '~/components/ui/table/PopoverCellEditor';
import {
  sortedByLength,
  type TallyBundleCount,
} from '~/components/common/tallyBundleCounts';
import { formatQuantityCell, productLabel } from '~/utils';
import { RandomTallyEditor, TallyCountEditor } from './BuyListTallyEditors';
import { useTallyLabel } from './useTallyLabel';
import type { BuyListDetailTally, CannedBuyListDetailTally } from './types';

type BuyListTallyCardProps = {
  tally: BuyListDetailTally;
  canManage: boolean;
  onRemove: (tally: BuyListDetailTally) => void;
  isRemoving: boolean;
  onSaveTallyCount: (
    tally: CannedBuyListDetailTally,
    tallyCount: number,
    onSaved: () => void
  ) => void;
  onSaveRandomBundles: (
    tally: BuyListDetailTally,
    bundles: TallyBundleCount[],
    onSaved: () => void
  ) => void;
  isSaving: boolean;
};

export function BuyListTallyCard({
  tally,
  canManage,
  onRemove,
  isRemoving,
  onSaveTallyCount,
  onSaveRandomBundles,
  isSaving,
}: Readonly<BuyListTallyCardProps>) {
  const { t } = useTranslation('inventory');
  const [isEditing, setIsEditing] = useState(false);
  const savedCannedTally =
    tally.__typename === 'CannedBuyListTally' ? tally : null;
  const stopEditing = () => setIsEditing(false);
  const product = productLabel(tally.platformProduct);
  const tallyLabel = useTallyLabel();
  const tallyName = tallyLabel(tally);
  const editLabel = t('inventory.buy_list.tallies.edit_action', { product });
  const removeLabel = t('inventory.buy_list.tallies.remove_action', {
    product,
  });
  const editButton = (
    <button
      type="button"
      className="cursor-pointer text-neut-600 hover:text-prim"
      aria-label={editLabel}
      title={editLabel}
      aria-expanded={isEditing}
      data-testid={`buy-list-tally-edit-${tally.id}`}
      onClick={() => setIsEditing((wasEditing) => !wasEditing)}
    >
      <Pencil size={14} aria-hidden="true" />
    </button>
  );

  return (
    <article
      className="rounded-md border border-neut-border"
      data-testid={`buy-list-tally-${tally.id}`}
    >
      <header className="flex items-center gap-3 border-b border-neut-border bg-neut-bg-alt px-3 py-2 text-sm">
        <span className="font-medium text-neut-prim">{product}</span>
        <span
          className="text-neut-sec"
          data-testid={`buy-list-tally-label-${tally.id}`}
        >
          {tallyName}
        </span>
        <span
          className="ml-auto text-neut-prim"
          data-testid={`buy-list-tally-quantity-${tally.id}`}
        >
          {formatQuantityCell(tally.quantity.value, tally.quantity.unit)}
        </span>
        {canManage && (
          <span className="flex items-center gap-2">
            {savedCannedTally ? (
              <PopoverCellEditor
                isOpen={isEditing}
                onDone={stopEditing}
                canClose={!isSaving}
                anchor={editButton}
                editor={
                  isEditing ? (
                    <TallyCountEditor
                      tallyCount={savedCannedTally.tallyCount}
                      isSaving={isSaving}
                      onSave={(tallyCount) =>
                        onSaveTallyCount(
                          savedCannedTally,
                          tallyCount,
                          stopEditing
                        )
                      }
                      onCancel={stopEditing}
                    />
                  ) : null
                }
              />
            ) : (
              editButton
            )}
            <button
              type="button"
              className="cursor-pointer text-neut-600 hover:text-error-700 disabled:opacity-50"
              aria-label={removeLabel}
              title={removeLabel}
              data-testid={`buy-list-tally-remove-${tally.id}`}
              disabled={isRemoving}
              onClick={() => onRemove(tally)}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          </span>
        )}
      </header>
      {isEditing && !savedCannedTally ? (
        <RandomTallyEditor
          tally={tally}
          isSaving={isSaving}
          onSave={(bundles) => onSaveRandomBundles(tally, bundles, stopEditing)}
          onCancel={stopEditing}
        />
      ) : (
        <ul className="divide-y divide-neut-border text-sm">
          {sortedByLength(tally.bundles).map((bundle) => (
            <li
              key={bundle.platformProductsLength.id}
              className="grid grid-cols-3 gap-3 px-3 py-1.5"
              data-testid={`buy-list-tally-bundle-${tally.id}-${bundle.platformProductsLength.id}`}
            >
              <span className="text-neut-prim">
                {bundle.platformProductsLength.lengthDisplayValue}
              </span>
              <span
                className="text-neut-sec"
                data-testid="buy-list-tally-row-bundle-count"
              >
                {t('inventory.buy_list.tallies.bundle_count', {
                  count: bundle.bundleCount,
                })}
              </span>
              <span className="text-right text-neut-prim">
                {formatQuantityCell(
                  bundle.quantity.value,
                  bundle.quantity.unit
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
